import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Check, ArrowRight } from "lucide-react";
import { mockData } from "../mock";

const CertificationTiersSection = () => {
  const { certificationTiers } = mockData;

  return (
    <section className="py-20 relative overflow-hidden bg-white">
      {/* Clean Professional Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-50/50 to-white"></div>
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          {/* Clean Header - Welcome.com Style */}
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Choose Your
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600"> 
              {" "}Excellence Level
            </span>
          </h2>
          
          {/* Professional Subtitle */}
          <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto mb-8 leading-relaxed font-medium">
            Three pathways to sustainability leadership, designed for organizations at every stage of their conscious business journey
          </p>
          
          <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
          {certificationTiers.tiers.map((tier, index) => {
            const gradients = [
              'from-blue-500 to-blue-600',
              'from-purple-500 to-purple-600', 
              'from-green-500 to-green-600'
            ];
            const bgGradients = [
              'from-blue-50 to-blue-100/50',
              'from-purple-50 to-purple-100/50',
              'from-green-50 to-green-100/50'
            ];
            
            return (
              <Card 
                key={tier.id}
                className={`relative group hover:shadow-xl transition-all duration-500 border-0 bg-gradient-to-br ${bgGradients[index]} backdrop-blur-sm ${
                  tier.popular ? "ring-2 ring-purple-500/20 scale-[1.02]" : ""
                }`}
              >
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                    <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white px-4 py-1.5 rounded-full text-sm font-semibold shadow-lg">
                      Most Popular
                    </div>
                  </div>
                )}

                <CardHeader className="text-center pb-6 pt-8">
                  <div className={`w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br ${gradients[index]} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <img 
                      src={tier.badgeImage} 
                      alt={`${tier.name} Badge`}
                      className="w-10 h-10 object-contain filter brightness-0 invert"
                    />
                  </div>
                  <CardTitle className="text-2xl font-bold text-gray-900 mb-3">
                    {tier.name}
                  </CardTitle>
                  <p className="text-gray-600 leading-relaxed">
                    {tier.description}
                  </p>
                </CardHeader>

                <CardContent className="pt-0">
                  <div className="space-y-3 mb-8">
                    {tier.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-start space-x-3">
                        <div className={`w-5 h-5 rounded-full bg-gradient-to-r ${gradients[index]} flex items-center justify-center mt-0.5 flex-shrink-0`}>
                          <Check className="h-3 w-3 text-white" />
                        </div>
                        <span className="text-gray-700 text-sm leading-relaxed">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="mb-6 p-4 bg-white/60 rounded-xl border border-gray-100">
                    <p className="text-sm text-gray-600 font-semibold mb-2">Perfect for:</p>
                    <p className="text-gray-800 text-sm leading-relaxed">{tier.eligibility}</p>
                  </div>

                  <a href="/signup">
                    <Button 
                      className={`w-full bg-gradient-to-r ${gradients[index]} hover:shadow-lg text-white transition-all duration-300 font-semibold py-3`}
                    >
                      Start {tier.name} Journey
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </a>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Clean CTA Section */}
        <div className="text-center bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-2xl p-8 max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Ready to Begin Your Sustainability Journey?
          </h3>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Join thousands of organizations building a more conscious future. Start your certification today.
          </p>
          <a href="/signup">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Start Your Journey
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </a>
          <p className="text-gray-500 mt-4 text-sm">
            <a href="/certification#tiers" className="hover:text-blue-600 transition-colors font-medium">Compare all tiers</a> 
            <span className="mx-2">•</span> 
            <a href="/pricing" className="hover:text-blue-600 transition-colors font-medium">View pricing details</a>
          </p>
        </div>
      </div>
    </section>
  );
};

export default CertificationTiersSection;